import styled from "styled-components";
import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/utils/store";

import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import { SortDirection } from "@/utils/enums";
import { SortState, TestingCompetitionResource } from "@/types/resources";
import Text from "@/components/Text";
import ProjectsTableRow from "./ProjectsTableRow";
import { Controls, ControlsButton } from "@/components/Controls";
import SearchBar from "@/components/SearchBar";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import ProjectsTeamFilter from "./ProjectsTeamFilter";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "name",
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    font-weight: 700;
    &:nth-child(1) {
      width: calc(29% - 56px);
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 18%;
    }
    &:nth-child(3) {
      width: 11%;
    }
    &:nth-child(4) {
      width: 12%;
    }
    &:nth-child(5) {
      width: 12%;
    }
    &:nth-child(6) {
      width: 18%;
    }
    &:last-child {
      width: 56px;
      padding-right: 16px;
      vertical-align: middle;
    }
  }
`;

const ProjectsTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [search, setSearch] = useState("");

  const [activeTeam, setActiveTeam] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const { status } = useParams();

  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery<TestingCompetitionResource[]>(
    [
      "gamification_projects",
      {
        type: "thumbnails",
        status,
        ...(debouncedSearch.length > 2 && {
          search: debouncedSearch,
        }),
        sort,
        activeTeam,
      },
    ],
    () =>
      api.get<never, TestingCompetitionResource[]>(
        "/v1/gamification/thumbnails/competitions",
        {
          params: {
            ...(debouncedSearch.length > 2 && {
              "filter[search]": debouncedSearch,
            }),
            "filter[status]": status,
            ...(sort.direction !== "DEFAULT" && {
              sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
            }),
            ...(activeTeam && {
              "filter[teamId]": activeTeam,
            }),
          },
        }
      ),
    {
      staleTime: 1000 * 60,
    }
  );

  const userTeams = useSelector((state: RootState) => state.auth.user?.teams);

  const userTeamsIds = userTeams?.map((team) => team.id);

  const filteredProjects = projects?.filter((project) =>
    userTeamsIds?.includes(project.teamId)
  );

  return (
    <>
      <TableHeader>
        <Controls>
          <NavLink
            to="/general/gamification/projects/active"
            children={({ isActive }) => (
              <ControlsButton active={isActive} as="span">
                Active Competitions
              </ControlsButton>
            )}
          />
          <NavLink
            to="/general/gamification/projects/ended"
            children={({ isActive }) => (
              <ControlsButton active={isActive} as="span">
                Closed Competitions
              </ControlsButton>
            )}
          />
        </Controls>
        <ProjectsTeamFilter
          activeTeam={activeTeam}
          setActiveTeam={setActiveTeam}
        />
      </TableHeader>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Project ID or Name"
      />
      <SectionHeading>
        {status === "active" ? "Active" : "Closed"} competitions list
      </SectionHeading>
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>
              Project Name
              <br />
              <Text as="span" $color="gray" $type="extraSmall">
                Project ID
              </Text>
            </Th>
            <SortableTh
              property="teamName"
              title="Team"
              setSort={setSort}
              sort={sort}
            />
            <Th>Status</Th>
            <SortableTh
              property="testSequence"
              title="Test Sequence"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="totalVariations"
              title="Total Variations"
              setSort={setSort}
              sort={sort}
            />
            <Th>Participants & CTR (%)</Th>
            <Th>
              <SrOnly>Link to a project</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            isLoading={isLoading}
            colSpan={7}
            isError={isError}
            hasData={!!filteredProjects?.length}
          >
            {filteredProjects?.map((project, index) => (
              <ProjectsTableRow key={index} project={project} />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
    </>
  );
};

export default ProjectsTable;
